import { createSelector } from '@reduxjs/toolkit';

import type {
    AccentEnum,
    AIVoiceFilter,
    AIVoiceStyleEnum,
    AIVoiceToneEnum,
    LanguageEnum,
} from 'js/src/client';
import { AIVoiceFilterTypeEnum } from 'js/src/client';
import type { RootState } from 'js/src/libs/RootState';

function getAIVoiceFiltersState(state: RootState) {
    return state.aiVoicesAndAvatars.aiVoiceFilters;
}

const createFilterSelector = (filterType: AIVoiceFilterTypeEnum) =>
    createSelector(
        (state: RootState) => getAIVoiceFiltersState(state).data[filterType],
        (data): AIVoiceFilter[] => data.allIds.map((id) => data.byId[id]),
    );

export const getAIVoiceLanguageFilters = createFilterSelector(AIVoiceFilterTypeEnum.LANGUAGE);
export const getAIVoiceAccentFilters = createFilterSelector(AIVoiceFilterTypeEnum.ACCENT);
export const getAIVoiceToneFilters = createFilterSelector(AIVoiceFilterTypeEnum.TONE);
export const getAIVoiceStyleFilters = createFilterSelector(AIVoiceFilterTypeEnum.STYLE);
export const getAIVoiceAgeFilters = createFilterSelector(AIVoiceFilterTypeEnum.AGE);
export const getAIVoiceGenderFilters = createFilterSelector(AIVoiceFilterTypeEnum.GENDER);
export const getAIVoiceQualityFilters = createFilterSelector(AIVoiceFilterTypeEnum.QUALITY);

function getAIVoiceFiltersRequestState(state: RootState) {
    return getAIVoiceFiltersState(state).request;
}

export function getAIVoiceFiltersRequestIsUninitialized(state: RootState): boolean {
    return getAIVoiceFiltersRequestState(state).status === 'uninitialized';
}

export function getAIVoiceFiltersRequestIsPending(state: RootState): boolean {
    return getAIVoiceFiltersRequestState(state).status === 'pending';
}

export function getAIVoiceFiltersRequestIsSuccess(state: RootState): boolean {
    return getAIVoiceFiltersRequestState(state).status === 'fulfilled';
}

export function getAIVoiceFiltersRequestIsError(state: RootState): boolean {
    return getAIVoiceFiltersRequestState(state).status === 'rejected';
}

export function getAIVoiceFiltersRequestError(state: RootState): string | null {
    return getAIVoiceFiltersRequestState(state).error;
}

type FilterOption<T> = { display: string; value: T };

const createTypedFilterSelector = <T>(filterType: AIVoiceFilterTypeEnum) =>
    createSelector(
        (state: RootState) => getAIVoiceFiltersState(state).data[filterType],
        (data): FilterOption<T>[] =>
            data.allIds.map((id) => {
                const filter = data.byId[id];
                return { display: filter.display, value: filter.value as T };
            }),
    );

/** @deprecated Use getAIVoiceAccentFilters instead */
export const getAIVoiceAccents = createTypedFilterSelector<AccentEnum>(
    AIVoiceFilterTypeEnum.ACCENT,
);

/** @deprecated Use getAIVoiceLanguageFilters instead */
export const getAIVoiceLanguages = createTypedFilterSelector<LanguageEnum>(
    AIVoiceFilterTypeEnum.LANGUAGE,
);

/** @deprecated Use getAIVoiceToneFilters instead */
export const getAIVoiceTones = createTypedFilterSelector<AIVoiceToneEnum>(
    AIVoiceFilterTypeEnum.TONE,
);

/** @deprecated Use getAIVoiceStyleFilters instead */
export const getAIVoiceStyles = createTypedFilterSelector<AIVoiceStyleEnum>(
    AIVoiceFilterTypeEnum.STYLE,
);

function getAIVoicesFiltersState(state: RootState) {
    return state.aiVoicesAndAvatars.aiVoices.data.filters;
}

/** @deprecated */
export function getSelectedAccent(state: RootState): AccentEnum | null {
    return getAIVoicesFiltersState(state).accent as AccentEnum | null;
}

/** @deprecated */
export function getSelectedLanguages(state: RootState): LanguageEnum[] {
    return getAIVoicesFiltersState(state).languages as LanguageEnum[];
}

/** @deprecated */
export function getSelectedTone(state: RootState): AIVoiceToneEnum | null {
    return getAIVoicesFiltersState(state).tone as AIVoiceToneEnum | null;
}

/** @deprecated */
export function getSelectedStyle(state: RootState): AIVoiceStyleEnum | null {
    return getAIVoicesFiltersState(state).style as AIVoiceStyleEnum | null;
}
